import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, getDocs, doc, getDoc, setDoc, updateDoc, deleteDoc, query, where, orderBy, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Swal from 'sweetalert2';
import AdminLayout from './AdminLayout';
import { useSelectedLocation } from './LocationContext';

const TimesheetsPage = ({ embedded = false }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payPeriods, setPayPeriods] = useState([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState('');
  const [employees, setEmployees] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationRule, setLocationRule] = useState(null);
  const [expandedEmployee, setExpandedEmployee] = useState(null);

  // Pay period search range — default to ±30 days from today
  const addDaysISO = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().split('T')[0];
  };
  const [periodSearchFrom, setPeriodSearchFrom] = useState(addDaysISO(-30));
  const [periodSearchTo, setPeriodSearchTo] = useState(addDaysISO(30));
  const [periodStatusFilter, setPeriodStatusFilter] = useState('open');

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTimesheet, setEditingTimesheet] = useState(null);
  const [editEmployeeId, setEditEmployeeId] = useState('');
  const [editForm, setEditForm] = useState({
    date: '',
    clockIn: '',
    clockOut: '',
    manualRegular: '',
    manualOvertime: '',
    manualDoubleTime: '',
  });
  const [savingTimesheet, setSavingTimesheet] = useState(false);

  const navigate = useNavigate();
  const auth = getAuth();
  const { selectedLocation } = useSelectedLocation() || {};

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await loadInitialData(currentUser.uid);
      } else {
        navigate('/admin/login');
      }
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Compute visible periods based on status filter and auto-select
  useEffect(() => {
    if (!payPeriods.length) {
      setSelectedPeriodId('');
      setTimesheets([]);
      return;
    }
    const visible = periodStatusFilter === 'all'
      ? payPeriods
      : payPeriods.filter(p => p.status === periodStatusFilter);
    if (visible.length === 0) {
      setSelectedPeriodId('');
      setTimesheets([]);
      return;
    }
    // If current selection is still visible, keep it
    const stillVisible = selectedPeriodId && visible.some(p => p.id === selectedPeriodId);
    if (!stillVisible) {
      setSelectedPeriodId(visible[0].id);
      // Load timesheets for the newly selected period
      if (user) {
        loadTimesheetsForPeriod(user.uid, visible[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payPeriods, periodStatusFilter]);

  const loadInitialData = async (uid) => {
    setLoading(true);
    try {
      const db = getFirestore();

      // Load locations for name mapping
      const locSnap = await getDocs(collection(db, 'users', uid, 'locations'));
      setLocations(locSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Load active employees FIRST into a local variable (fix stale state bug)
      const empSnap = await getDocs(
        query(
          collection(db, 'users', uid, 'employees'),
          where('status', '==', 'active'),
          orderBy('name')
        )
      );
      let emps = empSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      if (selectedLocation?.id) {
        emps = emps.filter(e => e.locationId === selectedLocation.id);
      }
      setEmployees(emps);

      // Load location overtime rules for pay calculations
      if (selectedLocation?.id) {
        try {
          const ruleDoc = await getDoc(doc(db, 'users', uid, 'locationRules', selectedLocation.id));
          if (ruleDoc.exists()) {
            setLocationRule(ruleDoc.data());
          } else {
            setLocationRule(null);
          }
        } catch (e) {
          console.error('Error loading location rules:', e);
          setLocationRule(null);
        }
      }

      // Load pay periods within default date range (±30 days) to avoid loading all history
      const defaultFrom = addDaysISO(-30);
      const defaultTo = addDaysISO(30);
      const periodsSnap = await getDocs(
        query(
          collection(db, 'users', uid, 'payPeriods'),
          where('startDate', '>=', defaultFrom),
          where('startDate', '<=', defaultTo),
          orderBy('startDate', 'asc')
        )
      );
      let periods = periodsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Filter by selected location
      if (selectedLocation?.id) {
        periods = periods.filter(p => p.locationId === selectedLocation.id);
      }
      setPayPeriods(periods);
      // Auto-selection is handled by useEffect watching payPeriods + statusFilter
    } catch (e) {
      console.error('Error loading timesheet data:', e);
      Swal.fire('Error', 'Failed to load timesheet data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const searchPayPeriods = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const db = getFirestore();

      // Reload employees to ensure fresh list
      const empSnap = await getDocs(
        query(
          collection(db, 'users', user.uid, 'employees'),
          where('status', '==', 'active'),
          orderBy('name')
        )
      );
      let emps = empSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      if (selectedLocation?.id) {
        emps = emps.filter(e => e.locationId === selectedLocation.id);
      }
      setEmployees(emps);

      // Reload location rules
      if (selectedLocation?.id) {
        try {
          const ruleDoc = await getDoc(doc(db, 'users', user.uid, 'locationRules', selectedLocation.id));
          if (ruleDoc.exists()) {
            setLocationRule(ruleDoc.data());
          } else {
            setLocationRule(null);
          }
        } catch (e) {
          console.error('Error loading location rules:', e);
          setLocationRule(null);
        }
      }

      let q = query(
        collection(db, 'users', user.uid, 'payPeriods'),
        orderBy('startDate', 'asc')
      );
      if (periodSearchFrom) {
        q = query(q, where('startDate', '>=', periodSearchFrom));
      }
      if (periodSearchTo) {
        q = query(q, where('startDate', '<=', periodSearchTo));
      }
      const snap = await getDocs(q);
      let periods = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      if (selectedLocation?.id) {
        periods = periods.filter(p => p.locationId === selectedLocation.id);
      }
      setPayPeriods(periods);
      // Auto-select first visible period after filter is applied by useEffect
    } catch (e) {
      console.error('Error searching pay periods:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadTimesheetsForPeriod = async (uid, period, employeeList = employees) => {
    if (!period) return;
    try {
      const db = getFirestore();
      // Query timesheets within the pay period date range only
      const tsSnap = await getDocs(
        query(
          collection(db, 'users', uid, 'employeeTimesheets'),
          where('date', '>=', period.startDate),
          where('date', '<=', period.endDate),
          orderBy('date', 'asc')
        )
      );
      let ts = tsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Filter timesheets by employee location using the provided list
      if (selectedLocation?.id && employeeList?.length > 0) {
        const employeeIdsAtLocation = new Set(employeeList.filter(e => e.locationId === selectedLocation.id).map(e => e.id));
        ts = ts.filter(t => employeeIdsAtLocation.has(t.employeeId));
      }
      setTimesheets(ts);
    } catch (e) {
      console.error('Error loading timesheets:', e);
      setTimesheets([]);
    }
  };

  const handlePeriodChange = async (periodId) => {
    setSelectedPeriodId(periodId);
    setExpandedEmployee(null);
    const period = payPeriods.find(p => p.id === periodId);
    if (period && user) {
      await loadTimesheetsForPeriod(user.uid, period);
    }
  };

  const handleClosePeriod = async () => {
    if (!selectedPeriod || !user) return;
    if (selectedPeriod.status === 'closed') {
      Swal.fire('Already Closed', 'This pay period is already closed.', 'info');
      return;
    }
    const result = await Swal.fire({
      title: 'Close Pay Period?',
      text: `Close "${selectedPeriod.payPeriodName || `${selectedPeriod.startDate} to ${selectedPeriod.endDate}`}"? Once closed, no more timesheets can be added or edited.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ff6b35',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Close Period',
    });
    if (!result.isConfirmed) return;

    try {
      const db = getFirestore();
      await setDoc(doc(db, 'users', user.uid, 'payPeriods', selectedPeriod.id), {
        status: 'closed',
        closedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });
      Swal.fire('Closed!', 'Pay period has been closed.', 'success');
      // Refresh periods list
      await searchPayPeriods();
    } catch (e) {
      console.error('Error closing pay period:', e);
      Swal.fire('Error', e.message || 'Failed to close period', 'error');
    }
  };

  const selectedPeriod = payPeriods.find(p => p.id === selectedPeriodId);

  // Generate all dates in the selected pay period range
  const getDatesInRange = (startDate, endDate) => {
    const dates = [];
    const start = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      dates.push(d.toISOString().split('T')[0]);
    }
    return dates;
  };

  const allPeriodDates = selectedPeriod
    ? getDatesInRange(selectedPeriod.startDate, selectedPeriod.endDate)
    : [];

  // Group timesheets by employee
  const timesheetsByEmployee = timesheets.reduce((acc, ts) => {
    if (!acc[ts.employeeId]) acc[ts.employeeId] = [];
    acc[ts.employeeId].push(ts);
    return acc;
  }, {});

  // Multipliers from location rules (defaults match Flutter)
  const multReg = locationRule?.multReg ?? 1.0;
  const multOt = locationRule?.multOt ?? 1.5;
  const multDt = locationRule?.multDt ?? 2.0;

  const computePay = (payRate, regular, overtime, doubleTime) => {
    const rate = payRate ?? 0;
    return (regular * rate * multReg) + (overtime * rate * multOt) + (doubleTime * rate * multDt);
  };

  // Calculate employee totals from actual entries only
  const employeeSummaries = employees.map((emp) => {
    const entries = timesheetsByEmployee[emp.id] || [];
    const totalRegular = entries.reduce((sum, e) => sum + (e.regularHours || 0), 0);
    const totalOvertime = entries.reduce((sum, e) => sum + (e.overtimeHours || 0), 0);
    const totalDoubleTime = entries.reduce((sum, e) => sum + (e.doubleTimeHours || 0), 0);
    const totalRaw = entries.reduce((sum, e) => sum + (e.rawHours || 0), 0);
    const totalHours = totalRegular + totalOvertime + totalDoubleTime;
    const totalPay = computePay(emp.payRate, totalRegular, totalOvertime, totalDoubleTime);
    const daysWorked = new Set(entries.map(e => e.date)).size;
    const hasOpenEntry = entries.some(e => e.clockIn && !e.clockOut);
    return {
      employeeId: emp.id,
      employeeName: emp.name || 'Unknown',
      entries,
      totalRegular: parseFloat(totalRegular.toFixed(2)),
      totalOvertime: parseFloat(totalOvertime.toFixed(2)),
      totalDoubleTime: parseFloat(totalDoubleTime.toFixed(2)),
      totalRaw: parseFloat(totalRaw.toFixed(2)),
      totalHours: parseFloat(totalHours.toFixed(2)),
      totalPay: parseFloat(totalPay.toFixed(2)),
      daysWorked,
      hasOpenEntry,
    };
  }).sort((a, b) => a.employeeName.localeCompare(b.employeeName));

  // Build day rows for an employee (all period days with or without entries)
  const buildEmployeeDayRows = (summary) => {
    const entryMap = {};
    (summary.entries || []).forEach(e => {
      entryMap[e.date] = e;
    });
    return allPeriodDates.map(date => ({
      date,
      entry: entryMap[date] || null,
      dayOfWeek: new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' }),
    }));
  };

  // Grand totals
  const grandTotalRegular = employeeSummaries.reduce((s, e) => s + e.totalRegular, 0);
  const grandTotalOvertime = employeeSummaries.reduce((s, e) => s + e.totalOvertime, 0);
  const grandTotalDoubleTime = employeeSummaries.reduce((s, e) => s + e.totalDoubleTime, 0);
  const grandTotalRaw = employeeSummaries.reduce((s, e) => s + e.totalRaw, 0);
  const grandTotalHours = employeeSummaries.reduce((s, e) => s + e.totalHours, 0);
  const grandTotalPay = employeeSummaries.reduce((s, e) => s + e.totalPay, 0);
  const totalEmployees = employeeSummaries.length;
  const totalEntries = timesheets.length;

  // Edit modal handlers
  const openAddModal = (employeeId, date) => {
    setEditingTimesheet(null);
    setEditEmployeeId(employeeId);
    setEditForm({
      date: date || '',
      clockIn: '',
      clockOut: '',
      manualRegular: '',
      manualOvertime: '',
      manualDoubleTime: '',
    });
    setShowEditModal(true);
  };

  const openEditModal = (entry, employeeId) => {
    setEditingTimesheet(entry);
    setEditEmployeeId(employeeId);
    const formatTimeInput = (val) => {
      if (!val) return '';
      try {
        const d = val.toDate ? val.toDate() : new Date(val);
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        return `${hh}:${mm}`;
      } catch {
        return '';
      }
    };
    setEditForm({
      date: entry.date || '',
      clockIn: formatTimeInput(entry.clockIn),
      clockOut: formatTimeInput(entry.clockOut),
      // Only prepopulate manual fields if this entry was previously manually overridden
      manualRegular: entry.manualOverride && entry.regularHours !== undefined ? String(entry.regularHours) : '',
      manualOvertime: entry.manualOverride && entry.overtimeHours !== undefined ? String(entry.overtimeHours) : '',
      manualDoubleTime: entry.manualOverride && entry.doubleTimeHours !== undefined ? String(entry.doubleTimeHours) : '',
    });
    setShowEditModal(true);
  };

  const handleSaveTimesheet = async () => {
    if (!user || !editEmployeeId || !editForm.date) {
      Swal.fire('Error', 'Date and employee are required', 'error');
      return;
    }
    setSavingTimesheet(true);
    try {
      const db = getFirestore();
      const emp = employees.find(e => e.id === editEmployeeId);
      const now = new Date();

      // Build timestamps from date + time strings
      const buildTimestamp = (dateStr, timeStr) => {
        if (!dateStr || !timeStr) return null;
        const [h, m] = timeStr.split(':').map(Number);
        const d = new Date(dateStr + 'T00:00:00');
        d.setHours(h, m, 0, 0);
        return d;
      };

      const clockIn = buildTimestamp(editForm.date, editForm.clockIn);
      const clockOut = buildTimestamp(editForm.date, editForm.clockOut);

      const hasManualHours = editForm.manualRegular !== '' || editForm.manualOvertime !== '' || editForm.manualDoubleTime !== '';

      const baseData = {
        employeeId: editEmployeeId,
        employeeName: emp?.name || 'Unknown',
        date: editForm.date,
        clockIn: clockIn || null,
        clockOut: clockOut || null,
        updatedAt: now,
      };

      // If manual hours provided, apply override
      if (hasManualHours) {
        baseData.regularHours = editForm.manualRegular !== '' ? parseFloat(editForm.manualRegular) : 0;
        baseData.overtimeHours = editForm.manualOvertime !== '' ? parseFloat(editForm.manualOvertime) : 0;
        baseData.doubleTimeHours = editForm.manualDoubleTime !== '' ? parseFloat(editForm.manualDoubleTime) : 0;
        baseData.manualOverride = {
          by: user.uid,
          at: now.toISOString(),
          fields: ['regularHours', 'overtimeHours', 'doubleTimeHours'],
        };
      }

      // If manually punching clock (no POS session), flag it
      if (!editingTimesheet || (!editingTimesheet.posSessionId && (clockIn || clockOut))) {
        baseData.manualEntry = {
          by: user.uid,
          at: now.toISOString(),
        };
      }

      let timesheetId = editingTimesheet?.id;

      if (editingTimesheet) {
        // Update existing
        await updateDoc(doc(db, 'users', user.uid, 'employeeTimesheets', timesheetId), baseData);
      } else {
        // Create new
        const ref = doc(collection(db, 'users', user.uid, 'employeeTimesheets'));
        await setDoc(ref, {
          ...baseData,
          createdAt: now,
          posSessionId: null,
          scheduleTimeIn: null,
          scheduleTimeOut: null,
        });
        timesheetId = ref.id;
      }

      // Trigger backend calculation only if NO manual override was applied
      if (timesheetId && !hasManualHours) {
        try {
          const functions = getFunctions();
          const calcFn = httpsCallable(functions, 'calculateTimesheetHours');
          await calcFn({ timesheetId });
        } catch (calcErr) {
          console.error('Backend calculation error:', calcErr);
          Swal.fire({
            icon: 'warning',
            title: 'Auto-calculation failed',
            text: 'Hours were saved but could not be auto-calculated. You can manually edit the hours if needed.',
            timer: 4000,
            showConfirmButton: false,
          });
        }
      }

      setShowEditModal(false);
      setEditingTimesheet(null);

      // Refresh data
      if (selectedPeriod && user) {
        await loadTimesheetsForPeriod(user.uid, selectedPeriod);
      }

      Swal.fire({
        title: 'Saved!',
        text: editingTimesheet ? 'Timesheet updated.' : 'Timesheet created.',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (e) {
      console.error('Error saving timesheet:', e);
      Swal.fire('Error', 'Failed to save timesheet', 'error');
    } finally {
      setSavingTimesheet(false);
    }
  };

  const handleDeleteTimesheet = async (entry) => {
    if (!user || !entry?.id) return;
    const result = await Swal.fire({
      title: 'Delete Entry?',
      text: `Remove timesheet for ${formatDate(entry.date)}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Delete',
    });
    if (!result.isConfirmed) return;

    try {
      const db = getFirestore();
      await deleteDoc(doc(db, 'users', user.uid, 'employeeTimesheets', entry.id));

      // Refresh data
      if (selectedPeriod && user) {
        await loadTimesheetsForPeriod(user.uid, selectedPeriod);
      }

      Swal.fire({
        title: 'Deleted',
        text: 'Timesheet entry removed.',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (e) {
      console.error('Error deleting timesheet:', e);
      Swal.fire('Error', 'Failed to delete timesheet', 'error');
    }
  };

  const formatTime = (val) => {
    if (!val) return '—';
    if (typeof val === 'string') return val;
    try {
      const d = val.toDate ? val.toDate() : new Date(val);
      return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return String(val);
    }
  };

  const formatDate = (val) => {
    if (!val) return '—';
    try {
      const d = new Date(val + 'T00:00:00');
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return val;
    }
  };

  const formatHours = (h) => (h || 0).toFixed(2);

  const content = (
    <div className="timesheets-page">
      {/* Header & Period Selector */}
      <div className="row mb-4 align-items-end">
        <div className="col-lg-7 col-md-12 mb-3 mb-lg-0">
          <h4 className="font-weight-bold">Timesheets</h4>
          <div className="d-flex flex-wrap align-items-center">
            {selectedLocation?.id ? (
              <>
                <span className="badge px-2 py-1 mr-2" style={{ background: '#ff6b3520', color: '#ff6b35', borderRadius: 8 }}>
                  <i className="fas fa-store mr-1"></i>
                  {selectedLocation.companyName || selectedLocation.locationName}
                </span>
                <span className="text-muted mr-3">Review timecards for this location</span>
              </>
            ) : (
              <span className="text-muted mr-3">Review employee timecards by pay period</span>
            )}

            {/* Status filter inline */}
            <div className="btn-group btn-group-sm mr-3" style={{ borderRadius: 6, overflow: 'hidden' }}>
              {[
                { key: 'all', label: 'All' },
                { key: 'open', label: 'Open' },
                { key: 'processing', label: 'Processing' },
                { key: 'closed', label: 'Closed' },
              ].map(s => (
                <button
                  key={s.key}
                  className="btn btn-sm"
                  style={{
                    backgroundColor: periodStatusFilter === s.key ? '#ff6b35' : '#fff',
                    borderColor: '#ddd',
                    color: periodStatusFilter === s.key ? '#fff' : '#666',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    padding: '4px 12px',
                  }}
                  onClick={() => setPeriodStatusFilter(s.key)}
                >
                  {s.label}
                </button>
              ))}
            </div>

            {/* Date range search inline */}
            <div className="d-flex align-items-center">
              <input
                type="date"
                className="form-control form-control-sm"
                style={{ width: 125, fontSize: '0.75rem' }}
                value={periodSearchFrom}
                onChange={e => setPeriodSearchFrom(e.target.value)}
              />
              <span className="text-muted mx-1" style={{ fontSize: '0.75rem' }}>to</span>
              <input
                type="date"
                className="form-control form-control-sm mr-1"
                style={{ width: 125, fontSize: '0.75rem' }}
                value={periodSearchTo}
                onChange={e => setPeriodSearchTo(e.target.value)}
              />
              <button
                className="btn btn-sm btn-primary"
                style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', borderRadius: 6, fontSize: '0.75rem' }}
                onClick={searchPayPeriods}
                disabled={loading}
                title="Search pay periods"
              >
                <i className="fas fa-search"></i>
              </button>
            </div>
          </div>
        </div>
        <div className="col-lg-5 col-md-12 text-lg-right">
          {/* Pay Period Dropdown */}
          <div className="d-inline-block text-left" style={{ minWidth: 320, maxWidth: '100%' }}>
            <label className="small text-muted mb-1 d-block">
              Pay Period
              <span className="text-muted ml-1">({(periodStatusFilter === 'all' ? payPeriods : payPeriods.filter(p => p.status === periodStatusFilter)).length} visible)</span>
            </label>
            <select
              className="form-control"
              value={selectedPeriodId}
              onChange={e => handlePeriodChange(e.target.value)}
              disabled={loading || payPeriods.length === 0}
            >
              <option value="">{payPeriods.length === 0 ? 'No periods found' : '— Select pay period —'}</option>
              {(periodStatusFilter === 'all' ? payPeriods : payPeriods.filter(p => p.status === periodStatusFilter)).map(p => (
                <option key={p.id} value={p.id}>
                  {p.payPeriodName ? `${p.payPeriodName} — ` : ''}{formatDate(p.startDate)} to {formatDate(p.endDate)} [{p.status?.toUpperCase()}]
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Period Info Bar */}
      {selectedPeriod && (
        <div className="card mb-4" style={{ borderRadius: 16, border: 'none', background: '#fff8f5' }}>
          <div className="card-body py-3">
            <div className="row align-items-center">
              <div className="col-md-3">
                <span className="text-muted small">Period</span>
                <div className="font-weight-bold" style={{ fontSize: '1.1rem' }}>
                  {selectedPeriod.payPeriodName || `${selectedPeriod.startDate} → ${selectedPeriod.endDate}`}
                </div>
              </div>
              <div className="col-md-3">
                <span className="text-muted small">Range</span>
                <div className="font-weight-bold">
                  {formatDate(selectedPeriod.startDate)} — {formatDate(selectedPeriod.endDate)}
                </div>
              </div>
              <div className="col-md-2">
                <span className="text-muted small">Pay Date</span>
                <div className="font-weight-bold">
                  {selectedPeriod.payDate ? formatDate(selectedPeriod.payDate) : '—'}
                </div>
              </div>
              <div className="col-md-2">
                <span className="text-muted small">Location</span>
                <div className="font-weight-bold small">
                  {selectedPeriod.locationId ? (
                    <span><i className="fas fa-store mr-1 text-primary"></i>{(() => { const loc = locations.find(l => l.id === selectedPeriod.locationId); return loc ? loc.companyName || loc.locationName : '—'; })()}</span>
                  ) : '—'}
                </div>
              </div>
              <div className="col-md-2 text-md-right">
                <div className="d-flex flex-column align-items-md-end">
                  <span
                    className="badge badge-pill px-3 py-2 mb-2"
                    style={{
                      background:
                        selectedPeriod.status === 'open'
                          ? '#d4edda'
                          : selectedPeriod.status === 'processing'
                          ? '#fff3cd'
                          : '#e2e3e5',
                      color:
                        selectedPeriod.status === 'open'
                          ? '#155724'
                          : selectedPeriod.status === 'processing'
                          ? '#856404'
                          : '#383d41',
                    }}
                  >
                    {selectedPeriod.status === 'closed' && <i className="fas fa-lock mr-1"></i>}
                    {selectedPeriod.status}
                  </span>
                  {selectedPeriod.status !== 'closed' && (
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      style={{ borderRadius: 6, fontSize: '0.75rem' }}
                      onClick={handleClosePeriod}
                      title="Close this pay period to prevent further edits"
                    >
                      <i className="fas fa-lock mr-1"></i>Close Period
                    </button>
                  )}
                </div>
              </div>
            </div>
            {selectedPeriod.status === 'closed' && (
              <div className="mt-2 pt-2" style={{ borderTop: '1px dashed #ddd' }}>
                <span className="badge badge-secondary" style={{ fontSize: '0.75rem' }}>
                  <i className="fas fa-lock mr-1"></i>This pay period is closed. Editing is disabled.
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {selectedPeriod && (
        <div className="row mb-4">
          {[
            { label: 'Employees', value: totalEmployees, icon: 'fa-users', color: '#007bff' },
            { label: 'Entries', value: totalEntries, icon: 'fa-clipboard-list', color: '#6f42c1' },
            { label: 'Raw Hours', value: formatHours(grandTotalRaw), icon: 'fa-stopwatch', color: '#6c757d' },
            { label: 'Regular', value: formatHours(grandTotalRegular), icon: 'fa-clock', color: '#28a745' },
            { label: 'Overtime', value: formatHours(grandTotalOvertime), icon: 'fa-business-time', color: '#ff6b35' },
            { label: 'Double-Time', value: formatHours(grandTotalDoubleTime), icon: 'fa-angle-double-up', color: '#dc3545' },
            { label: 'Total Hrs', value: formatHours(grandTotalHours), icon: 'fa-hourglass-half', color: '#17a2b8' },
            { label: 'Total Pay', value: '$' + grandTotalPay.toFixed(2), icon: 'fa-dollar-sign', color: '#ff6b35' },
          ].map((kpi, idx) => (
            <div key={idx} className="col-md col-6 mb-3">
              <div className="card h-100" style={{ borderRadius: 16, border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <div className="card-body text-center p-3">
                  <div
                    className="d-flex align-items-center justify-content-center mx-auto mb-2"
                    style={{ width: 40, height: 40, borderRadius: 10, background: kpi.color + '15' }}
                  >
                    <i className={`fas ${kpi.icon}`} style={{ color: kpi.color, fontSize: 16 }}></i>
                  </div>
                  <h5 className="font-weight-bold m-0" style={{ fontSize: '1.1rem' }}>{kpi.value}</h5>
                  <small className="text-muted" style={{ fontSize: '0.75rem' }}>{kpi.label}</small>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Timesheet Table */}
      {selectedPeriod ? (
        employeeSummaries.length === 0 ? (
          <div className="text-center py-5">
            <i className="fas fa-clipboard-list fa-3x text-muted mb-3"></i>
            <h5 className="text-muted">
              {selectedLocation?.id ? `No employees at ${selectedLocation.companyName || selectedLocation.locationName}` : 'No employees for this location'}
            </h5>
            <p className="text-muted">Add employees to this location to track timecards.</p>
          </div>
        ) : (
          <div className="card" style={{ borderRadius: 16, border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0" style={{ fontSize: '0.9rem' }}>
                  <thead style={{ background: '#f8f9fa' }}>
                    <tr>
                      <th className="border-0 pl-4" style={{ width: 40 }}></th>
                      <th className="border-0">Employee</th>
                      <th className="border-0 text-center">Days</th>
                      <th className="border-0 text-center">Entries</th>
                      <th className="border-0 text-center">Raw</th>
                      <th className="border-0 text-center">Regular</th>
                      <th className="border-0 text-center">OT</th>
                    <th className="border-0 text-center">DT</th>
                    <th className="border-0 text-center">Total</th>
                    <th className="border-0 text-center">Pay</th>
                    <th className="border-0 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employeeSummaries.map((summary) => (
                      <React.Fragment key={summary.employeeId}>
                        {/* Summary Row */}
                        <tr
                          style={{ cursor: 'pointer' }}
                          onClick={() =>
                            setExpandedEmployee(
                              expandedEmployee === summary.employeeId ? null : summary.employeeId
                            )
                          }
                        >
                          <td className="pl-4 align-middle">
                            <i
                              className={`fas fa-chevron-${
                                expandedEmployee === summary.employeeId ? 'down' : 'right'
                              } text-muted`}
                              style={{ fontSize: 12 }}
                            ></i>
                          </td>
                          <td className="align-middle">
                            <div className="d-flex align-items-center">
                              <div
                                className="d-flex align-items-center justify-content-center mr-3"
                                style={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: '50%',
                                  background: '#ff6b35',
                                  color: '#fff',
                                  fontSize: '0.8rem',
                                  fontWeight: 'bold',
                                }}
                              >
                                {summary.employeeName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                              </div>
                              <div>
                                <div className="font-weight-bold">{summary.employeeName}</div>
                                <small className="text-muted">{summary.entries.length} record(s)</small>
                              </div>
                            </div>
                          </td>
                          <td className="text-center align-middle">{summary.daysWorked}</td>
                          <td className="text-center align-middle">{summary.entries.length}</td>
                          <td className="text-center align-middle text-muted">
                            {formatHours(summary.totalRaw)}
                          </td>
                          <td className="text-center align-middle font-weight-bold" style={{ color: '#28a745' }}>
                            {formatHours(summary.totalRegular)}
                          </td>
                          <td className="text-center align-middle font-weight-bold" style={{ color: '#ff6b35' }}>
                            {formatHours(summary.totalOvertime)}
                          </td>
                          <td className="text-center align-middle font-weight-bold" style={{ color: '#dc3545' }}>
                            {formatHours(summary.totalDoubleTime)}
                          </td>
                          <td className="text-center align-middle font-weight-bold">
                            {formatHours(summary.totalHours)}
                          </td>
                          <td className="text-center align-middle font-weight-bold" style={{ color: '#ff6b35' }}>
                            ${summary.totalPay.toFixed(2)}
                          </td>
                          <td className="text-center align-middle">
                            {summary.hasOpenEntry ? (
                              <span className="badge badge-warning">Open Shift</span>
                            ) : (
                              <span className="badge badge-success">Complete</span>
                            )}
                          </td>
                        </tr>

                        {/* Expanded Detail Rows — All Days */}
                        {expandedEmployee === summary.employeeId && (
                          <tr>
                            <td colSpan={11} className="p-0 border-0" style={{ background: '#f8f9fa' }}>
                              <div className="p-3">
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                  <h6 className="font-weight-bold text-muted m-0" style={{ fontSize: '0.85rem' }}>
                                    <i className="fas fa-list-ul mr-2"></i>Daily Timecards
                                  </h6>
                                  {(() => {
                                    const emp = employees.find(e => e.id === summary.employeeId);
                                    if (!emp || !emp.payRate) return null;
                                    return (
                                      <span className="badge badge-light border" style={{ fontSize: '0.75rem' }}>
                                        <i className="fas fa-dollar-sign mr-1 text-success"></i>
                                        Pay Rate: <strong>${parseFloat(emp.payRate).toFixed(2)}/hr</strong>
                                      </span>
                                    );
                                  })()}
                                  {selectedPeriod?.status === 'closed' ? (
                                    <span className="badge badge-secondary" style={{ fontSize: '0.75rem' }}>
                                      <i className="fas fa-lock mr-1"></i>Locked
                                    </span>
                                  ) : (
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      style={{ borderRadius: 8, fontSize: '0.8rem' }}
                                      onClick={() => openAddModal(summary.employeeId)}
                                    >
                                      <i className="fas fa-plus mr-1"></i>Add Entry
                                    </button>
                                  )}
                                </div>
                                <div className="table-responsive">
                                  <table className="table table-sm mb-0" style={{ fontSize: '0.85rem', background: '#fff', borderRadius: 12 }}>
                                    <thead style={{ background: '#e9ecef' }}>
                                      <tr>
                                        <th style={{ width: 100 }}>Date</th>
                                        <th style={{ width: 50 }}>Day</th>
                                        <th>Clock In</th>
                                        <th>Clock Out</th>
                                        <th className="text-center">Raw</th>
                                        <th className="text-center">Regular</th>
                                        <th className="text-center">OT</th>
                                        <th className="text-center">DT</th>
                                        <th className="text-center">Total</th>
                                        <th className="text-center">Pay</th>
                                        <th className="text-right">Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {buildEmployeeDayRows(summary).map((day) => {
                                        const entry = day.entry;
                                        const entryTotal = entry
                                          ? (entry.regularHours || 0) + (entry.overtimeHours || 0) + (entry.doubleTimeHours || 0)
                                          : 0;
                                        const entryPay = entry
                                          ? computePay(
                                              employees.find(e => e.id === summary.employeeId)?.payRate,
                                              entry.regularHours || 0,
                                              entry.overtimeHours || 0,
                                              entry.doubleTimeHours || 0
                                            )
                                          : 0;
                                        const isToday = day.date === new Date().toISOString().split('T')[0];
                                        return (
                                          <tr
                                            key={day.date}
                                            style={isToday ? { background: '#fff8f5' } : {}}
                                          >
                                            <td className="align-middle font-weight-bold">
                                              {formatDate(day.date)}
                                              {entry?.manualOverride && (
                                                <span className="badge badge-warning ml-1" style={{ fontSize: '0.6rem' }} title={`Manually fixed by ${entry.manualOverride.by} at ${new Date(entry.manualOverride.at).toLocaleString()}`}>M</span>
                                              )}
                                              {entry?.manualEntry && !entry?.manualOverride && (
                                                <span className="badge badge-info ml-1" style={{ fontSize: '0.6rem' }} title={`Manual punch by ${entry.manualEntry.by} at ${new Date(entry.manualEntry.at).toLocaleString()}`}>M</span>
                                              )}
                                            </td>
                                            <td className="align-middle text-muted">{day.dayOfWeek}</td>
                                            <td className="align-middle">
                                              {entry ? formatTime(entry.clockIn) : (
                                                <span className="text-muted">—</span>
                                              )}
                                            </td>
                                            <td className="align-middle">
                                              {entry ? (
                                                entry.clockOut ? formatTime(entry.clockOut) : (
                                                  <span className="badge badge-warning" style={{ fontSize: '0.7rem' }}>Open</span>
                                                )
                                              ) : (
                                                <span className="text-muted">—</span>
                                              )}
                                            </td>
                                            <td className="text-center align-middle text-muted">
                                              {entry ? formatHours(entry.rawHours) : '—'}
                                            </td>
                                            <td className="text-center align-middle">
                                              {entry ? formatHours(entry.regularHours) : '—'}
                                            </td>
                                            <td className="text-center align-middle">
                                              {entry ? formatHours(entry.overtimeHours) : '—'}
                                            </td>
                                            <td className="text-center align-middle">
                                              {entry ? formatHours(entry.doubleTimeHours) : '—'}
                                            </td>
                                            <td className="text-center align-middle font-weight-bold">
                                              {entry ? formatHours(entryTotal) : '—'}
                                            </td>
                                            <td className="text-center align-middle font-weight-bold" style={{ color: '#ff6b35' }}>
                                              {entry ? '$' + entryPay.toFixed(2) : '—'}
                                            </td>
                                            <td className="text-right align-middle">
                                              {selectedPeriod?.status === 'closed' ? (
                                                <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                  <i className="fas fa-lock mr-1"></i>
                                                </span>
                                              ) : entry ? (
                                                <div className="d-inline-flex">
                                                  <button
                                                    className="btn btn-sm btn-outline-primary mr-1"
                                                    style={{ borderRadius: 6, padding: '2px 6px', fontSize: '0.75rem' }}
                                                    onClick={() => openEditModal(entry, summary.employeeId)}
                                                    title="Edit"
                                                  >
                                                    <i className="fas fa-edit"></i>
                                                  </button>
                                                  <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    style={{ borderRadius: 6, padding: '2px 6px', fontSize: '0.75rem' }}
                                                    onClick={() => handleDeleteTimesheet(entry)}
                                                    title="Delete"
                                                  >
                                                    <i className="fas fa-trash"></i>
                                                  </button>
                                                </div>
                                              ) : (
                                                <button
                                                  className="btn btn-sm btn-outline-secondary"
                                                  style={{ borderRadius: 6, padding: '2px 6px', fontSize: '0.75rem' }}
                                                  onClick={() => openAddModal(summary.employeeId, day.date)}
                                                  title="Add entry"
                                                >
                                                  <i className="fas fa-plus"></i>
                                                </button>
                                              )}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                  <tfoot style={{ background: '#f8f9fa' }}>
                    <tr>
                      <td colSpan={4} className="pl-4 font-weight-bold text-right">Grand Total</td>
                      <td className="text-center font-weight-bold text-muted">{formatHours(grandTotalRaw)}</td>
                      <td className="text-center font-weight-bold" style={{ color: '#28a745' }}>{formatHours(grandTotalRegular)}</td>
                      <td className="text-center font-weight-bold" style={{ color: '#ff6b35' }}>{formatHours(grandTotalOvertime)}</td>
                      <td className="text-center font-weight-bold" style={{ color: '#dc3545' }}>{formatHours(grandTotalDoubleTime)}</td>
                      <td className="text-center font-weight-bold">{formatHours(grandTotalHours)}</td>
                      <td className="text-center font-weight-bold" style={{ color: '#ff6b35' }}>${grandTotalPay.toFixed(2)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        )
      ) : (
        <div className="text-center py-5">
          <i className="fas fa-calendar-alt fa-3x text-muted mb-3"></i>
          <h5 className="text-muted">Select a pay period</h5>
          <p className="text-muted">Choose a pay period above to view employee timesheets.</p>
        </div>
      )}

      {/* Edit / Add Modal */}
      {showEditModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
            <div className="modal-content" style={{ borderRadius: 16 }}>
              <div className="modal-header border-0">
                <h5 className="modal-title font-weight-bold">
                  {editingTimesheet ? 'Edit Timecard' : 'Add Timecard'}
                </h5>
                <button type="button" className="close" onClick={() => setShowEditModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label className="font-weight-bold">Employee</label>
                  <select
                    className="form-control"
                    value={editEmployeeId}
                    onChange={e => setEditEmployeeId(e.target.value)}
                    disabled={!!editingTimesheet}
                  >
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.name}</option>
                    ))}
                  </select>
                  {(() => {
                    const emp = employees.find(e => e.id === editEmployeeId);
                    if (!emp || !emp.payRate) return null;
                    const estPay = computePay(
                      emp.payRate,
                      parseFloat(editForm.manualRegular || 0),
                      parseFloat(editForm.manualOvertime || 0),
                      parseFloat(editForm.manualDoubleTime || 0)
                    );
                    return (
                      <div className="mt-1" style={{ fontSize: '0.8rem' }}>
                        <span className="text-muted">Pay Rate:</span>
                        <span className="font-weight-bold ml-1">${parseFloat(emp.payRate).toFixed(2)}/hr</span>
                        {(parseFloat(editForm.manualRegular || 0) > 0 || parseFloat(editForm.manualOvertime || 0) > 0 || parseFloat(editForm.manualDoubleTime || 0) > 0) && (
                          <span className="ml-2" style={{ color: '#ff6b35' }}>
                            → Est. Pay: <strong>${estPay.toFixed(2)}</strong>
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div className="form-group">
                  <label className="font-weight-bold">Date <span className="text-danger">*</span></label>
                  <input
                    type="date"
                    className="form-control"
                    value={editForm.date}
                    onChange={e => setEditForm({ ...editForm, date: e.target.value })}
                  />
                </div>
                <div className="row">
                  <div className="col-6">
                    <div className="form-group">
                      <label className="font-weight-bold">Clock In</label>
                      <input
                        type="time"
                        className="form-control"
                        value={editForm.clockIn}
                        onChange={e => setEditForm({ ...editForm, clockIn: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="form-group">
                      <label className="font-weight-bold">Clock Out</label>
                      <input
                        type="time"
                        className="form-control"
                        value={editForm.clockOut}
                        onChange={e => setEditForm({ ...editForm, clockOut: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
                <div className="alert alert-light border" style={{ borderRadius: 10, fontSize: '0.8rem' }}>
                  <i className="fas fa-info-circle mr-1 text-info"></i>
                  Leave Clock In/Out blank for an open shift. Rules will be recalculated automatically after saving.
                </div>

                {/* Manual hours override — fallback if auto-calculation fails */}
                <div style={{ borderTop: '1px dashed #ddd', paddingTop: 12, marginTop: 4 }}>
                  <label className="font-weight-bold text-muted" style={{ fontSize: '0.8rem' }}>
                    <i className="fas fa-wrench mr-1"></i>Manual Override (only if auto-calc fails)
                  </label>
                  <div className="row">
                    <div className="col-4">
                      <div className="form-group mb-2">
                        <label style={{ fontSize: '0.75rem' }}>Regular</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="form-control"
                          placeholder="0.00"
                          value={editForm.manualRegular}
                          onChange={e => setEditForm({ ...editForm, manualRegular: e.target.value })}
                          style={{ fontSize: '0.85rem' }}
                        />
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="form-group mb-2">
                        <label style={{ fontSize: '0.75rem' }}>OT</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="form-control"
                          placeholder="0.00"
                          value={editForm.manualOvertime}
                          onChange={e => setEditForm({ ...editForm, manualOvertime: e.target.value })}
                          style={{ fontSize: '0.85rem' }}
                        />
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="form-group mb-2">
                        <label style={{ fontSize: '0.75rem' }}>DT</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="form-control"
                          placeholder="0.00"
                          value={editForm.manualDoubleTime}
                          onChange={e => setEditForm({ ...editForm, manualDoubleTime: e.target.value })}
                          style={{ fontSize: '0.85rem' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer border-0">
                <button
                  className="btn btn-outline-secondary"
                  style={{ borderRadius: 8 }}
                  onClick={() => setShowEditModal(false)}
                  disabled={savingTimesheet}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary font-weight-bold"
                  style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', borderRadius: 8 }}
                  onClick={handleSaveTimesheet}
                  disabled={savingTimesheet}
                >
                  {savingTimesheet ? (
                    <span className="spinner-border spinner-border-sm mr-2"></span>
                  ) : (
                    <i className="fas fa-save mr-2"></i>
                  )}
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (embedded) return content;

  return (
    <AdminLayout title="Timesheets">
      <div className="container-fluid py-4">{content}</div>
    </AdminLayout>
  );
};

export default TimesheetsPage;
